import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithReAuth } from './api';

import { ChartDifferenceProps } from '../../components/Charts/chart.interface';
import { ComparedFrLineResponse } from '../../data-transformer/compared-fr-line-chart/compared-fr-line.interface';
import { getWidgetIdFromUrl } from '../../helpers/general-helpers';
import { OdinDataSort } from '@myosh/odin-components';
import { FieldFiltersProps } from './table-data.interfaces';
import { getFilters } from './get-filters';

export interface ChartQueryParameters {
  url?: string;
  widgetId?: string;
  from?: string;
  to?: string;
  viewId?: string;
}

export interface BasicDataChartResponse extends Record<string, unknown> {
  field?: string;
  secondField?: string;
  value?: string | number;
}

export interface ChartResponse extends BasicDataChartResponse {
  value_column?: number;
  localizedFieldCaption?: string;
}

export interface ChartColumnLineResponse {
  columnData: Record<string, Record<string, number>>;
  columnDataSum: Record<string, number>;
  lineData: Record<string, number>;
  lineDataCount: Record<string, number>;
  lineDataDenom: Record<string, number>;
  lineNumerate: Record<string, number>;
  xaxeValues: Array<string>;
  lineValues: number;
  calculationType: Record<string, number>;
}

export interface ValueKpi {
  value_KPI?: number;
}

export interface DataKPI extends Record<string, string | number | undefined> {
  name?: string;
  value_FR?: number;
  value_KPI?: number;
  value_TRIFR?: number;
}

export type RollingSiteTRIFR = Record<string, BasicDataChartResponse[]>;

export interface ChartDataKPI {
  basicValue: BasicDataChartResponse[];
  kpi?: BasicDataChartResponse[];
  rollingFR?: Array<Array<BasicDataChartResponse>>;
  rollingTRIFR?: BasicDataChartResponse[];
  rollingSiteTRIFR?: RollingSiteTRIFR;
}

export interface ColumnRateResponse {
  field: string;
  percentageValue: string;
  value: string;
  recordIds: Array<string>;
}

export interface MapChartData {
  lat: number;
  lon: number;
  recordIDs: string[];
  value: number;
}

export interface ColumnKpiPercentageResponse {
  field: string;
  value: string;
  percentage: string;
  kpi: string;
  color?: string;
}

export interface NCRLineChartResponse {
  [key: string]: {
    field: string;
    value: number;
  }[];
}

export type GetDetailsDataForChartColumnRateApiResponse = /** status 200 OK */ {
  [key: string]: string;
}[];
export type GetDetailsDataForChartColumnRateApiArg = {
  widgetId: string;
  page?: number;
  pageSize?: number;
  sortedFields?: Record<string, OdinDataSort>;
  fieldFilters?: FieldFiltersProps;

  /**
   * Accepts a list of filters, with valid operators being:
   * - equals (eq)
   * - like (like)
   * - greater than or equal (gte)
   * - less than or equal (lte)
   * - in (in)
   *
   * The format is: field:operator:filterValue.
   *
   * For the 'in' operator, the filters are to be separated by a comma only, no white spaces:
   *   field:in:filter1,filter2,filter3
   *
   * For filtering fields of type date, the format of the date should be yyyy-mm-dd.
   * The gte & lte operands are to be used only with fields of type date.
   *
   * For filtering with [blanks] option, the filter parameter should be passed like: %5Bblanks%5D.
   * The [blanks] option is supported only for eq & in operators.
   *
   * The like operator is case insensitive.
   * If no filter is given, the list of all records is to be returned.
   *
   * The field and the second field (if configured for the widget) have to be included in the filter parameter
   * when the call for the details window for the specific widget is made.
   * The usage is the same as for normal filters.
   *
   * For widget type = MAP, instead of field and second field, 'lat' & 'lon' fields have to be provided as filter fields.
   * These fields are only supported with 'eq' operator.
   *
   * For widget type = COLUMN, LINE, PIE, etc. with first and/or second fields being of type date,
   * the filtering should be done using the like operand when the date field contains only month and year,
   * and 'eq' operand when date filter contains year, month and day. Date format should be yyyy-mm-dd.
   *
   * When field/second field is stored as 'Dashboard Creation Date', during filtering it shall be used as 'Creation Date'.
   */
  filter?: string[];
  body: number[];
};

export const chartApi = createApi({
  reducerPath: 'chart',
  baseQuery: baseQueryWithReAuth,
  tagTypes: ['ChartResponse'],
  endpoints: (builder) => ({
    pieChart: builder.query<Array<ChartResponse>, ChartQueryParameters>({
      query: (args) => {
        const url = `/data/chart-pie/${args.widgetId}`;
        return { url, method: 'GET' };
      },
      providesTags: (result, error, { widgetId }) => [{ type: 'ChartResponse', id: widgetId }],
    }),
    lineChart: builder.query<Array<ChartResponse>, ChartQueryParameters>({
      query: (args) => {
        const url = `/data/chart-line/${args.widgetId}`;
        return { url, method: 'GET' };
      },
      providesTags: (result, error, { widgetId }) => [{ type: 'ChartResponse', id: widgetId }],
    }),
    columnLineChart: builder.query<ChartColumnLineResponse, ChartQueryParameters>({
      query: (args) => {
        const url = `/data/chart-column-line/${args.widgetId}`;
        return { url, method: 'GET' };
      },
      providesTags: (result, error, { widgetId }) => [{ type: 'ChartResponse', id: widgetId }],
    }),
    columnChart: builder.query<ChartDataKPI, ChartQueryParameters>({
      query: (args) => {
        const url = `/data/chart-column/${args.widgetId}`;
        return { url, method: 'GET' };
      },
      providesTags: (result, error, { widgetId }) => [{ type: 'ChartResponse', id: widgetId }],
    }),
    columnRate: builder.query<Array<ColumnRateResponse>, ChartQueryParameters>({
      query: (args) => {
        const url = `/data/chart-column-rate/${args.widgetId}`;
        return { url, method: 'GET' };
      },
      providesTags: (result, error, { widgetId }) => [{ type: 'ChartResponse', id: widgetId }],
    }),
    columnBasic: builder.query<Array<ChartResponse>, ChartQueryParameters>({
      query: (args) => {
        const url = `/data/chart-column-basic/${args.widgetId}`;
        return { url, method: 'GET' };
      },
      providesTags: (result, error, { widgetId }) => [{ type: 'ChartResponse', id: widgetId }],
    }),
    barChart: builder.query<Array<ChartResponse>, ChartQueryParameters>({
      query: (args) => {
        const url = `/data/chart-bar/${args.widgetId}`;
        return { url, method: 'GET' };
      },
      providesTags: (result, error, { widgetId }) => [{ type: 'ChartResponse', id: widgetId }],
      transformResponse(response: Array<ChartResponse>) {
        return response.map((item) => {
          return {
            ...item,
            field: item.field || '[none]',
          };
        });
      },
    }),
    chartKPI: builder.query<ChartDataKPI, ChartQueryParameters>({
      query: (args) => {
        const url = `/data/chart-kpi/${args.widgetId}`;
        return { url, method: 'GET' };
      },
      providesTags: (result, error, { widgetId }) => [{ type: 'ChartResponse', id: widgetId }],
    }),
    chartKPIPercentage: builder.query<Array<ColumnKpiPercentageResponse>, ChartQueryParameters>({
      query: (args) => {
        const url = `${args.url}`;
        return { url, method: 'GET' };
      },
      providesTags: (result, error, { url }) => [{ type: 'ChartResponse', id: getWidgetIdFromUrl(url) }],
    }),
    chartDifference: builder.query<ChartDifferenceProps, ChartQueryParameters>({
      query: (args) => {
        const url = `/data/chart-difference/${args.widgetId}`;
        return { url, method: 'GET' };
      },
      providesTags: (result, error, { widgetId }) => [{ type: 'ChartResponse', id: widgetId }],
    }),
    chartRollingFR: builder.query<BasicDataChartResponse[], ChartQueryParameters>({
      query: (args) => {
        const url = `/data/chart-rollingfr/${args.widgetId}`;
        return { url, method: 'GET' };
      },
      providesTags: (result, error, { widgetId }) => [{ type: 'ChartResponse', id: widgetId }],
    }),
    comparedFrLine: builder.query<ComparedFrLineResponse, ChartQueryParameters>({
      query: (args) => {
        const url = `/data/compared-fr-line/${args.widgetId}`;
        return { url, method: 'GET' };
      },
      providesTags: (result, error, { widgetId }) => [{ type: 'ChartResponse', id: widgetId }],
    }),
    mapWidget: builder.query<MapChartData[], ChartQueryParameters>({
      query: (args) => {
        const url = `/data/map/${args.widgetId}`;
        return { url, method: 'GET' };
      },
      providesTags: (result, error, { widgetId }) => [{ type: 'ChartResponse', id: widgetId }],
    }),
    ncrLineChart: builder.query<NCRLineChartResponse, ChartQueryParameters>({
      query: (args) => {
        const url = `${args.url}`;
        return { url, method: 'GET' };
      },
      providesTags: (result, error, { url }) => [{ type: 'ChartResponse', id: getWidgetIdFromUrl(url) }],
    }),
    getDetailsDataForChartColumnRate: builder.mutation<
      GetDetailsDataForChartColumnRateApiResponse,
      GetDetailsDataForChartColumnRateApiArg
    >({
      query: (queryArg) => {
        let url = `/data/chart-column-rate/${queryArg.widgetId}/details?`;
        if (queryArg.page) {
          url += `&page=${queryArg.page}`;
        }
        if (queryArg.pageSize) {
          url += `&pageSize=${queryArg.pageSize}`;
        }
        if (queryArg.sortedFields) {
          const sortField = Object.keys(queryArg.sortedFields);
          if (sortField.length >= 1) {
            const sortOrder = queryArg.sortedFields[sortField[0]].direction.toUpperCase();
            // the data can only be sorted by one field at a time
            url += `&sort=${sortField[0]}&orderBy=${sortOrder}`;
          }
        }
        if (queryArg.fieldFilters) {
          url += getFilters(queryArg.fieldFilters);
        }

        return {
          url: url,
          method: 'POST',
          body: queryArg.body,
        };
      },
    }),
  }),
});

export const {
  usePieChartQuery,
  useLineChartQuery,
  useColumnLineChartQuery,
  useColumnChartQuery,
  useBarChartQuery,
  useChartKPIQuery,
  useChartKPIPercentageQuery,
  useColumnRateQuery,
  useColumnBasicQuery,
  useMapWidgetQuery,
  useChartDifferenceQuery,
  useComparedFrLineQuery,
  useChartRollingFRQuery,
  useNcrLineChartQuery,
  useGetDetailsDataForChartColumnRateMutation,
} = chartApi;
